/**
 * Mock API Client
 * Drop-in replacement for the real API client that uses mock data
 */

import { mockApiService } from './mockApiService'
import { MockApiResponse } from './mockApi'

/**
 * Mock API Client that mimics the interface of the real API client
 * but returns mock data instead of making real HTTP requests
 */
export class MockApiClient {
  private baseURL: string
  private timeout: number

  constructor() {
    this.baseURL = '/api'
    this.timeout = 30000
  }

  /**
   * Convert mock API response to axios-like response format
   */
  private formatResponse<T>(mockResponse: MockApiResponse<T>) {
    if (!mockResponse.success) {
      const error = new Error(mockResponse.error || 'Mock API Error')
      ;(error as any).response = {
        status: 500,
        data: { error: mockResponse.error }
      }
      throw error
    }

    return {
      data: mockResponse.data,
      status: 200,
      statusText: 'OK',
      headers: {},
      config: {},
      request: {}
    }
  }

  /**
   * Mock GET requests
   */
  async get<T = any>(url: string, config?: any): Promise<any> {
    // Parse URL and route to appropriate mock service method
    const urlParts = url.split('?')
    const path = urlParts[0]
    const queryString = urlParts[1] || ''
    const params = new URLSearchParams(queryString)
    
    // Convert params to object
    const filters: any = {}
    params.forEach((value, key) => {
      filters[key] = isNaN(Number(value)) ? value : Number(value)
    })

    try {
      let mockResponse: MockApiResponse<any>

      // Route based on path
      if (path === '/auth/validate') {
        const token = config?.headers?.Authorization?.replace('Bearer ', '') || ''
        mockResponse = await mockApiService.validateToken(token)
      } else if (path === '/payments') {
        mockResponse = await mockApiService.getPayments(filters)
      } else if (path.startsWith('/payments/') && path.endsWith('/refresh')) {
        const id = path.split('/')[2]
        mockResponse = await mockApiService.refreshPaymentStatus(id)
      } else if (path.startsWith('/payments/')) {
        const id = path.split('/')[2]
        mockResponse = await mockApiService.getPayment(id)
      } else if (path === '/settlements') {
        mockResponse = await mockApiService.getSettlements(filters)
      } else if (path.startsWith('/settlements/') && path.endsWith('/report')) {
        // Mock blob response for reports
        const blob = new Blob(['Mock settlement report'], { type: 'application/pdf' })
        return { data: blob, status: 200, statusText: 'OK', headers: {}, config: {}, request: {} }
      } else if (path.startsWith('/settlements/')) {
        const id = path.split('/')[2]
        mockResponse = await mockApiService.getSettlement(id)
      } else if (path === '/dashboard/stats') {
        mockResponse = await mockApiService.getDashboardStats()
      } else if (path === '/dashboard/chart') {
        const period = filters.period || '7d'
        mockResponse = await mockApiService.getDashboardChartData(period)
      } else if (path === '/providers/status') {
        mockResponse = await mockApiService.getProviderStatuses()
      } else if (path.startsWith('/providers/') && path.endsWith('/status')) {
        const provider = path.split('/')[2]
        mockResponse = await mockApiService.getProviderStatus(provider)
      } else if (path === '/providers/failover-history') {
        const limit = filters.limit || 10
        mockResponse = await mockApiService.getFailoverHistory(limit)
      } else if (path.startsWith('/providers/') && path.endsWith('/metrics')) {
        const provider = path.split('/')[2]
        const period = filters.period || '24h'
        mockResponse = await mockApiService.getProviderMetrics(provider, period)
      } else if (path === '/rates') {
        mockResponse = await mockApiService.getExchangeRates()
      } else if (path.startsWith('/rates/')) {
        const stablecoin = path.split('/')[2]
        mockResponse = await mockApiService.getExchangeRate(stablecoin)
      } else if (path === '/calculator/costs') {
        const amount = filters.amount || 10000
        const stablecoin = filters.stablecoin || 'USDT'
        const settlementRail = filters.settlementRail || 'FPS'
        mockResponse = await mockApiService.calculateCosts(amount, stablecoin, settlementRail)
      } else if (path === '/calculator/compare') {
        const amount = filters.amount || 10000
        const stablecoin = filters.stablecoin || 'USDT'
        const rails = ['FPS', 'CIPS', 'e-CNY']
        const comparisons: any = {}
        for (const rail of rails) {
          const calculation = await mockApiService.calculateCosts(amount, stablecoin, rail)
          comparisons[rail] = calculation.data
        }
        mockResponse = {
          success: true,
          data: comparisons,
          timestamp: new Date().toISOString(),
          processingTime: 400
        }
      } else if (path.startsWith('/invoices/') && path.endsWith('/payments')) {
        const invoiceId = path.split('/')[2]
        // Mock payments for specific invoice
        mockResponse = {
          success: true,
          data: [],
          timestamp: new Date().toISOString(),
          processingTime: 200
        }
      } else if (path.startsWith('/customer/')) {
        // Handle customer payment endpoints
        if (path.startsWith('/customer/invoices/')) {
          const invoiceId = path.split('/')[3]
          mockResponse = {
            success: true,
            data: {
              id: invoiceId,
              invoiceNumber: 'INV-2024-0001',
              amount: 10000,
              currency: 'HKD',
              description: 'Demo Invoice',
              dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
              status: 'sent',
              businessName: 'Demo Business Ltd.',
              paymentUrl: `/pay/${invoiceId}`
            },
            timestamp: new Date().toISOString(),
            processingTime: 200
          }
        } else if (path === '/customer/stablecoins') {
          mockResponse = {
            success: true,
            data: [
              { type: 'USDT', name: 'Tether USD', symbol: 'USDT', network: 'Ethereum', isActive: true, minAmount: 10, maxAmount: 100000 },
              { type: 'USDC', name: 'USD Coin', symbol: 'USDC', network: 'Ethereum', isActive: true, minAmount: 10, maxAmount: 100000 },
              { type: 'HKD₮', name: 'HKD Stablecoin', symbol: 'HKD₮', network: 'Ethereum', isActive: true, minAmount: 100, maxAmount: 500000 },
              { type: 'CNH₮', name: 'CNH Stablecoin', symbol: 'CNH₮', network: 'Ethereum', isActive: true, minAmount: 100, maxAmount: 500000 }
            ],
            timestamp: new Date().toISOString(),
            processingTime: 150
          }
        } else {
          mockResponse = {
            success: true,
            data: { message: `Mock customer endpoint response for ${path}` },
            timestamp: new Date().toISOString(),
            processingTime: 200
          }
        }
      } else {
        // Default response for unknown endpoints
        mockResponse = {
          success: true,
          data: { message: `Mock response for ${path}` },
          timestamp: new Date().toISOString(),
          processingTime: 200
        }
      }

      return this.formatResponse(mockResponse)
    } catch (error) {
      throw error
    }
  }

  /**
   * Mock POST requests
   */
  async post<T = any>(url: string, data?: any, config?: any): Promise<any> {
    try {
      let mockResponse: MockApiResponse<any>

      // Route based on path
      if (url === '/auth/login') {
        mockResponse = await mockApiService.login(data.email, data.password)
      } else if (url === '/auth/refresh') {
        mockResponse = await mockApiService.validateToken(data.refreshToken)
      } else if (url === '/auth/logout') {
        mockResponse = {
          success: true,
          data: { message: 'Logged out successfully' },
          timestamp: new Date().toISOString(),
          processingTime: 100
        }
      } else if (url === '/auth/register') {
        mockResponse = {
          success: true,
          data: { message: 'Registration successful' },
          timestamp: new Date().toISOString(),
          processingTime: 500
        }
      } else if (url === '/payments') {
        mockResponse = await mockApiService.createPayment(data)
      } else if (url === '/settlements/batch') {
        mockResponse = await mockApiService.createSettlementBatch()
      } else if (url.startsWith('/payments/') && url.endsWith('/refresh')) {
        const id = url.split('/')[2]
        mockResponse = await mockApiService.refreshPaymentStatus(id)
      } else if (url.startsWith('/providers/') && url.endsWith('/health-check')) {
        const provider = url.split('/')[2]
        mockResponse = await mockApiService.performHealthCheck(provider)
      } else if (url === '/providers/recommend') {
        mockResponse = await mockApiService.getRecommendedProvider(data.stablecoin, data.amount, data.settlementRail)
      } else if (url === '/providers/force-failover') {
        mockResponse = await mockApiService.forceFailover(data.fromProvider, data.reason)
      } else if (url.startsWith('/providers/') && url.endsWith('/reset')) {
        const provider = url.split('/')[2]
        mockResponse = await mockApiService.resetProviderStatus(provider)
      } else if (url.startsWith('/customer/')) {
        // Handle customer payment POST endpoints
        if (url === '/customer/conversion-rate') {
          const rate = Math.random() * 0.5 + 7.5 // 7.5-8.0 HKD per USD
          mockResponse = {
            success: true,
            data: {
              id: `rate_${Math.random().toString(36).substr(2, 9)}`,
              fromCurrency: 'HKD',
              toCurrency: data.stablecoinType,
              rate,
              lockedUntil: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
              stablecoinAmount: Math.round((10000 / rate) * 1000000) / 1000000
            },
            timestamp: new Date().toISOString(),
            processingTime: 300
          }
        } else if (url === '/customer/payments') {
          mockResponse = {
            success: true,
            data: {
              paymentId: `pay_${Math.random().toString(36).substr(2, 9)}`,
              stablecoinAmount: Math.round(Math.random() * 1000 * 1000000) / 1000000,
              recipientAddress: '0x' + Array.from({ length: 40 }, () => Math.floor(Math.random() * 16).toString(16)).join(''),
              qrCode: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==',
              provider: data.onRampProvider === 'fps' ? 'HashKey' : 'Moonpay',
              expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
            },
            timestamp: new Date().toISOString(),
            processingTime: 400
          }
        } else {
          mockResponse = {
            success: true,
            data: { message: `Mock customer POST response for ${url}`, submittedData: data },
            timestamp: new Date().toISOString(),
            processingTime: 300
          }
        }
      } else {
        // Default response for unknown POST endpoints
        mockResponse = {
          success: true,
          data: { message: `Mock POST response for ${url}`, submittedData: data },
          timestamp: new Date().toISOString(),
          processingTime: 300
        }
      }

      return this.formatResponse(mockResponse)
    } catch (error) {
      throw error
    }
  }

  /**
   * Mock PUT requests
   */
  async put<T = any>(url: string, data?: any, config?: any): Promise<any> {
    // For demo purposes, most PUT requests will return success
    const mockResponse: MockApiResponse<any> = {
      success: true,
      data: { message: `Mock PUT response for ${url}`, updatedData: data },
      timestamp: new Date().toISOString(),
      processingTime: 250
    }

    return this.formatResponse(mockResponse)
  }

  /**
   * Mock PATCH requests
   */
  async patch<T = any>(url: string, data?: any, config?: any): Promise<any> {
    // For demo purposes, most PATCH requests will return success
    const mockResponse: MockApiResponse<any> = {
      success: true,
      data: { message: `Mock PATCH response for ${url}`, patchedData: data },
      timestamp: new Date().toISOString(),
      processingTime: 200
    }

    return this.formatResponse(mockResponse)
  }

  /**
   * Mock DELETE requests
   */
  async delete<T = any>(url: string, config?: any): Promise<any> {
    // For demo purposes, most DELETE requests will return success
    const mockResponse: MockApiResponse<any> = {
      success: true,
      data: { message: `Mock DELETE response for ${url}` },
      timestamp: new Date().toISOString(),
      processingTime: 150
    }

    return this.formatResponse(mockResponse)
  }
}

// Export singleton instance
export const mockApiClient = new MockApiClient()